import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import apiClient from '@/api/client'
import type { ThemePreference, User } from '@/types'

type Theme = ThemePreference

interface ThemeContextValue {
  theme: Theme
  setTheme: (theme: Theme) => void
  /** Same as `theme` — kept on the context for backwards compatibility with consumers. */
  resolvedTheme: Theme
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

const STORAGE_KEY = 'pos-theme'
const DEFAULT_THEME: Theme = 'light'
const VALID_THEMES: Theme[] = ['light', 'dark', 'high-contrast']

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (VALID_THEMES as string[]).includes(value)
}

function applyTheme(theme: Theme) {
  const root = document.documentElement
  root.classList.remove('dark', 'high-contrast')
  if (theme === 'dark') {
    root.classList.add('dark')
  } else if (theme === 'high-contrast') {
    root.classList.add('high-contrast')
  }
}

/** Pull a cached theme from the stored user record so first paint matches the DB. */
function readStoredUserTheme(): Theme | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem('pos_user')
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<User>
    return isTheme(parsed.theme_preference) ? parsed.theme_preference : null
  } catch {
    return null
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return DEFAULT_THEME
    // Prefer the per-user cached value (set on login) over the device-local
    // legacy key, so theme follows the user across stations on first paint.
    // Fall back to Light — never to OS preference, so a dark-mode laptop still
    // opens the POS bright by default.
    const fromUser = readStoredUserTheme()
    if (fromUser) return fromUser
    const stored = localStorage.getItem(STORAGE_KEY)
    return isTheme(stored) ? stored : DEFAULT_THEME
  })

  // Skip the very first server PATCH on initial mount — we read from cache,
  // we didn't change anything. Without this, every cold load writes the
  // already-stored value back to the DB.
  const hasMounted = useRef(false)

  const setTheme = (newTheme: Theme) => {
    if (!isTheme(newTheme)) return
    setThemeState(newTheme)
    localStorage.setItem(STORAGE_KEY, newTheme)

    // Mirror into the cached user record so the next reload reflects it
    // before the /auth/me round-trip lands.
    try {
      const raw = localStorage.getItem('pos_user')
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, unknown>
        parsed.theme_preference = newTheme
        localStorage.setItem('pos_user', JSON.stringify(parsed))
      }
    } catch {
      /* ignore — cache hydration is best-effort */
    }

    // Persist server-side. Failures are logged but not surfaced: a preference
    // that's right on this device but stale in the DB is a far better UX than
    // a toast on theme switch. Skip when unauthenticated.
    if (!localStorage.getItem('pos_token')) return
    apiClient.updateThemePreference(newTheme).catch((err) => {
      console.warn('Theme preference sync failed:', err)
    })
  }

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  // Hydrate from the server once on mount — the DB is authoritative across
  // devices. If unauthenticated (login screen) we skip; the next login will
  // populate pos_user with the right theme_preference.
  useEffect(() => {
    hasMounted.current = true
    if (!localStorage.getItem('pos_token')) return
    let cancelled = false
    apiClient
      .getCurrentUser()
      .then((res) => {
        if (cancelled) return
        const serverTheme = res?.data?.theme_preference
        if (isTheme(serverTheme) && serverTheme !== theme) {
          setThemeState(serverTheme)
          localStorage.setItem(STORAGE_KEY, serverTheme)
        }
      })
      .catch(() => {
        /* offline or 401 — keep local theme */
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme: theme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
